/* eslint-disable react/prop-types */
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { getColor } from 'helpers/utils';
import React, { useEffect, useState } from 'react';
import { round } from 'lodash';
import { Card, Col, Form, Row } from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent,
  DatasetComponent
]);

const tooltipFormatter = (params, unit, colors) => {
  return `<div>
    <h6 class="fs--2 mb-0 fw-semi-bold">${params[0].axisValueLabel}</h6>
    <div class="align-items-center">
      ${params
        .map((param, index) => {
          const value = param.value.toFixed(2);
          const color = colors[param.seriesIndex];
          return `
          <p class='mb-0 text-600 fs--1'>
            <span style='display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 5px;'></span>
            ${param.seriesName}: <span class='text-800 fw-semi-bold fs--1'>${value} <span class='text-500 fw-normal fs--1'>${unit}</span></span>
          </p>`;
        })
        .join('')}
    </div>
  </div>`;
};

const getOption = (result, colors, seriesNames, unit) => {
  // Prepare the series data based on the result and seriesNames
  const legendData = Object.values(result).map(item => item.name);

  // Prepare the series data
  const series = legendData.map((name, index) => {
    return {
      name: name,
      type: 'bar',
      data: seriesNames.map(seriesName => {
        // Find the corresponding data for this series name
        const dataItem = Object.values(result).find(item => item.name === name);
        return dataItem ? dataItem.data[seriesNames.indexOf(seriesName)] : 0;
      })
    };
  });

  return {
    color: colors,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: params => tooltipFormatter(params, unit, colors)
    },
    grid: {
      right: '5%',
      left: '5%',
      bottom: '5%',
      containLabel: true
    },
    legend: {
      data: legendData,
      textStyle: {
        color: getColor('gray-700')
      },
      icon: 'circle',
      inactiveColor: getColor('gray-400')
    },
    xAxis: [
      {
        type: 'category',
        axisTick: {
          alignWithLabel: true
        },
        data: seriesNames
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLine: {
          show: true
        },
        axisLabel: {
          formatter: `{value} ${unit}`
        }
      }
    ],
    series: series
  };
};

const MultiParamSeasonalChart = ({ chartData }) => {
  let results = chartData?.data;
  let unit = chartData?.unit;
  let variant = chartData?.variant;
  let seriesNames = chartData?.series;
  const [selectedYear, setSelectedYear] = useState(Object.keys(variant)[0]);
  const [selectedSeason, setSelectedSeason] = useState(
    variant[selectedYear][0]
  );
  const [selectedData, setSelectedData] = useState([]);
  const colors = ['#5470C6', '#91CC75', '#EE6666', '#FF5733'];

  const handleSelectChange = () => {
    const selectedKey = `${selectedYear}:${selectedSeason}`;
    const selectedData = results[selectedKey] || {};
    setSelectedData(selectedData);
  };

  useEffect(() => {
    if (selectedSeason && selectedYear) {
      handleSelectChange();
    }
  }, []);
  useEffect(() => {
    if (selectedSeason && selectedYear) {
      handleSelectChange();
    }
  }, [selectedSeason, selectedYear]);

  const handleYearChange = e => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    setSelectedSeason(variant[newYear][0]); // Update the season based on the selected year
  };

  const handleSeasonChange = e => {
    setSelectedSeason(e.target.value);
  };

  return (
    <Card className="h-100">
      <FalconCardHeader
        title={chartData.label || ''}
        titleTag="h5"
        light={true}
        desc={chartData?.desc}
        endEl={
          variant && (
            <Row className="align-items-center">
              <Col className="col-auto">
                <Form.Select value={selectedYear} onChange={handleYearChange}>
                  {Object.keys(variant).map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col className="col-auto">
                <Form.Select
                  value={selectedSeason}
                  onChange={handleSeasonChange}
                  id="season"
                  name="season"
                >
                  {variant[selectedYear].map(season => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          )
        }
      />
      <Card.Body className="h-100 position-relative" dir="ltr">
        <ReactEChartsCore
          echarts={echarts}
          option={getOption(selectedData, colors, unit, seriesNames)}
          style={{ minHeight: '25.75rem' }}
        />
        <p
          style={{
            fontSize: '12px',
            position: 'absolute',
            top: '20px',
            right: '20px'
          }}
        >
          {unit}
        </p>
      </Card.Body>
    </Card>
  );
};

export default MultiParamSeasonalChart;
